import { useForm, router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Trash2 } from 'lucide-react'
import { NotaryAvailability } from '@/types'

type Props = {
  availabilities: NotaryAvailability[]
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
]

function dayLabel(dayOfWeek: number) {
  return DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.label ?? `Day ${dayOfWeek}`
}

function groupByDay(availabilities: NotaryAvailability[]) {
  const grouped: Record<number, NotaryAvailability[]> = {}
  for (const a of availabilities) {
    if (!grouped[a.day_of_week]) grouped[a.day_of_week] = []
    grouped[a.day_of_week].push(a)
  }
  return grouped
}

function handleDelete(id: string) {
  if (!confirm('Remove this availability slot?')) return
  router.delete(`/notary_portal/availabilities/${id}`)
}

export default function AvailabilitiesIndex({ availabilities }: Props) {
  const grouped = groupByDay(availabilities)

  const { data, setData, post, processing, errors, reset } = useForm({
    day_of_week: '1',
    start_time: '09:00',
    end_time: '17:00',
    timezone: 'America/New_York',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/notary_portal/availabilities', {
      data: {
        availability: {
          ...data,
          day_of_week: parseInt(data.day_of_week, 10),
        },
      },
      onSuccess: () => reset(),
    })
  }

  return (
    <NotaryPortalLayout title="Availability">
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Availability</h1>
          <p className="text-sm text-muted-foreground">Manage your weekly availability for appointments</p>
        </div>

        {/* Weekly grid */}
        <div>
          <SectionHeader title="Weekly Schedule" count={availabilities.length} />
          <div className="mt-3 space-y-1">
            {DAYS_OF_WEEK.map((day) => {
              const slots = grouped[day.value] || []
              return (
                <div key={day.value} className="flex items-start gap-4 py-2.5 border-b border-border/40 last:border-0">
                  <span className="w-24 text-sm font-medium text-foreground shrink-0 pt-0.5">{day.label}</span>
                  <div className="flex-1">
                    {slots.length === 0 ? (
                      <span className="text-sm text-muted-foreground">No slots</span>
                    ) : (
                      <div className="space-y-1">
                        {slots.map((slot) => (
                          <div key={slot.id} className="flex items-center gap-2 group">
                            <span className="text-sm">
                              {slot.start_time} &ndash; {slot.end_time}
                            </span>
                            <span className="text-[12px] text-muted-foreground">{slot.timezone}</span>
                            <button
                              type="button"
                              onClick={() => handleDelete(slot.id)}
                              className="opacity-0 group-hover:opacity-100 ml-1 text-muted-foreground hover:text-destructive transition-all"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Add slot form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormSection title="Add Time Slot">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Day</label>
                <Select value={data.day_of_week} onValueChange={(v) => setData('day_of_week', v)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d) => (
                      <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.day_of_week && <p className="text-[12px] text-destructive">{errors.day_of_week}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Timezone</label>
                <Select value={data.timezone} onValueChange={(v) => setData('timezone', v)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.timezone && <p className="text-[12px] text-destructive">{errors.timezone}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Start Time</label>
                <Input
                  className="h-8 text-sm"
                  type="time"
                  value={data.start_time}
                  onChange={(e) => setData('start_time', e.target.value)}
                />
                {errors.start_time && <p className="text-[12px] text-destructive">{errors.start_time}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">End Time</label>
                <Input
                  className="h-8 text-sm"
                  type="time"
                  value={data.end_time}
                  onChange={(e) => setData('end_time', e.target.value)}
                />
                {errors.end_time && <p className="text-[12px] text-destructive">{errors.end_time}</p>}
              </div>
            </div>
          </FormSection>

          <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">
            Add Slot
          </Button>
        </form>
      </div>
    </NotaryPortalLayout>
  )
}
